import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, CircularProgress, Container, Paper, Typography } from "@mui/material";
import MenuHeader from "./MenuHeader";
import PaymentLinkService from "../services/payment_link.service";

const PHONE_DISPLAY = "+41 22 548 33 37";
const PHONE_HREF = "tel:+41225483337";

const STATUS_COPY = {
  loading: {
    title: "redirect_title",
    body: "redirect_body",
  },
  already_paid: {
    title: "already_paid_title",
    body: "already_paid_body",
  },
  processing: {
    title: "processing_title",
    body: "processing_body",
  },
  failed_retry_available: {
    title: "failed_title",
    body: "failed_body",
  },
  expired: {
    title: "expired_title",
    body: "expired_body",
  },
  not_payable: {
    title: "not_payable_title",
    body: "not_payable_body",
  },
};

const PaymentLinkResolver = () => {
  const { token } = useParams();
  const { t } = useTranslation("payment_link");
  const [status, setStatus] = useState("loading");
  const [retrying, setRetrying] = useState(false);
  const resolvedTokenRef = useRef(null);
  const retryingRef = useRef(false);

  const copy = useMemo(() => STATUS_COPY[status] || STATUS_COPY.not_payable, [status]);

  const applyResponse = useCallback((response) => {
    if (
      ["redirect_to_payment", "processing"].includes(response?.status) &&
      response?.payment_url
    ) {
      setStatus("loading");
      window.location.assign(response.payment_url);
      return;
    }

    setStatus(response?.status || "not_payable");
  }, []);

  const resolveLink = useCallback(async () => {
    if (!token) {
      setStatus("expired");
      return;
    }

    try {
      setStatus("loading");
      const response = await PaymentLinkService.resolve(token);
      applyResponse(response);
    } catch (error) {
      setStatus(PaymentLinkService.statusFromError(error) || "not_payable");
    }
  }, [applyResponse, token]);

  useEffect(() => {
    if (resolvedTokenRef.current === token) return;
    resolvedTokenRef.current = token;
    resolveLink();
  }, [resolveLink, token]);

  const handleRetry = async () => {
    if (!token || retryingRef.current) return;

    try {
      retryingRef.current = true;
      setRetrying(true);
      const response = await PaymentLinkService.retry(token);
      applyResponse(response);
    } catch (error) {
      setStatus(PaymentLinkService.statusFromError(error) || "failed_retry_available");
    } finally {
      retryingRef.current = false;
      setRetrying(false);
    }
  };

  const isLoading = status === "loading";
  const canRetry = status === "failed_retry_available";

  return (
    <>
      <MenuHeader />
      <Box
        sx={{
          minHeight: "calc(100vh - 86px)",
          background: "#f8f8f6",
          display: "flex",
          alignItems: "center",
          py: { xs: 4, md: 8 },
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              borderRadius: "18px",
              px: { xs: 3, md: 6 },
              py: { xs: 5, md: 7 },
              textAlign: "center",
              border: "1px solid rgba(57, 61, 68, 0.08)",
              boxShadow: "0 18px 50px rgba(57, 61, 68, 0.08)",
            }}
          >
            {isLoading && (
              <CircularProgress
                size={42}
                sx={{ color: "#393D44", mb: 3 }}
              />
            )}

            <Typography
              variant="h5"
              component="h1"
              sx={{ fontWeight: 600, color: "#393D44", mb: 2 }}
            >
              {t(copy.title)}
            </Typography>

            <Typography
              sx={{
                color: "rgba(57, 61, 68, 0.78)",
                lineHeight: 1.7,
                fontSize: "16px",
                mb: canRetry ? 4 : 3,
              }}
            >
              {t(copy.body)}
            </Typography>

            {canRetry && (
              <Button
                variant="contained"
                disabled={retrying}
                onClick={handleRetry}
                sx={{
                  textTransform: "none",
                  borderRadius: "15px",
                  minHeight: "52px",
                  px: 5,
                  mb: 4,
                  background: "#393D44",
                  color: "#FAEF9B",
                  "&:hover": { background: "#2f3339" },
                }}
              >
                {retrying ? t("retry_loading") : t("retry_button")}
              </Button>
            )}

            <Typography sx={{ color: "#393D44", fontSize: "15px" }}>
              {t("contact_prefix")}{" "}
              <Box
                component="a"
                href={PHONE_HREF}
                sx={{
                  color: "#393D44",
                  fontWeight: 700,
                  textDecoration: "underline",
                }}
              >
                {PHONE_DISPLAY}
              </Box>
            </Typography>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default PaymentLinkResolver;
